import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { SelectOption } from '@/shared/ui/filters';
import { useProjectSiteParams, useUrlState } from '@/shared/lib/hooks';
import { useDebounce } from '@/shared/lib/hooks/use-debounce';

import { getTransportModeOptions } from '@/features/dashboard-filters';
import { useGetOrganizationQuery, useGetOrganizationsQuery, type Organization } from '@/entities/organizations';
import { useGetDashboardOptionsQuery } from '@/entities/dashboard';

import { DEFAULT_MAPPING_URL_STATE, type MappingUrlState } from '../model/url-state';
import { buildMappingFilterChips } from '../model/build-filter-chips';

const toOrganizationOption = (org: Organization): SelectOption => ({
  value: org.id.toString(),
  label: org.name,
  keywords: org.abbreviation ? [org.abbreviation] : undefined
});

export function useMappingFilters() {
  const { t } = useTranslation();

  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_MAPPING_URL_STATE);
  const { projectId, siteId } = useProjectSiteParams();

  const selectedVehicleCategories = useMemo(() => {
    return urlState.vehicleCategories ? urlState.vehicleCategories.split(',').filter(Boolean) : [];
  }, [urlState.vehicleCategories]);

  const [organizationSearch, setOrganizationSearch] = useState('');

  const debouncedOrganizationSearch = useDebounce(organizationSearch, 300);

  // The map has no Apply button, so an undebounced consignment number would fire
  // a round of map queries on every keystroke. The input still shows it instantly.
  const debouncedConsignmentNumber = useDebounce(urlState.consignmentNumber, 300);

  const { data: optionsData } = useGetDashboardOptionsQuery({ projectId, siteId });

  const { data: organizationsData, isLoading: isLoadingOrganizations } = useGetOrganizationsQuery({
    search: debouncedOrganizationSearch || undefined,
    projectId
  });

  const listedOrganizationOptions: SelectOption[] = useMemo(() => {
    if (!organizationsData?.organizations) return [];

    return organizationsData.organizations.map(toOrganizationOption);
  }, [organizationsData]);

  // The search endpoint caps its result at the first 100 organizations, so a
  // deep-linked or restored organizationId is often missing from the list and the
  // combobox renders blank while the data is in fact filtered (353351). Fetch that
  // one organization by id to recover its label.
  const isSelectedOrganizationListed = listedOrganizationOptions.some((opt) => opt.value === urlState.organizationId);

  const { data: selectedOrganizationData } = useGetOrganizationQuery(
    { id: Number(urlState.organizationId), projectId, siteId },
    { skip: !urlState.organizationId || isSelectedOrganizationListed }
  );

  const organizationOptions: SelectOption[] = useMemo(() => {
    if (isSelectedOrganizationListed || !selectedOrganizationData) return listedOrganizationOptions;

    return [toOrganizationOption(selectedOrganizationData), ...listedOrganizationOptions];
  }, [listedOrganizationOptions, isSelectedOrganizationListed, selectedOrganizationData]);

  const vehicleCategoryOptions: SelectOption[] = useMemo(() => {
    const vehicleCategories = optionsData?.vehicleCategories || [];
    // Remove the 'None' option (first item) as it's not needed for the filter
    const options = getTransportModeOptions(t, vehicleCategories);
    return options.filter((opt) => opt.value !== 'NO');
  }, [t, optionsData?.vehicleCategories]);

  const filterValues = useMemo(
    () => ({
      consignmentNumber: urlState.consignmentNumber || undefined,
      organizationId: urlState.organizationId || undefined,
      // Include vehicle categories if they have values
      ...(selectedVehicleCategories.length > 0 ? { vehicleCategories: selectedVehicleCategories } : {})
    }),
    [urlState.consignmentNumber, urlState.organizationId, selectedVehicleCategories]
  );

  const activeFiltersCount = useMemo(() => {
    return Number(!!urlState.consignmentNumber) + Number(!!urlState.organizationId) + selectedVehicleCategories.length;
  }, [urlState.consignmentNumber, urlState.organizationId, selectedVehicleCategories]);

  const hasActiveFilters = activeFiltersCount > 0;

  const setConsignmentNumber = useCallback(
    (value: string) => {
      setUrlState({ consignmentNumber: value });
    },
    [setUrlState]
  );

  const setSelectedOrganization = useCallback(
    (value?: string) => {
      setUrlState({ organizationId: value || '' });
    },
    [setUrlState]
  );

  const setSelectedVehicleCategories = useCallback(
    (values: string[]) => {
      setUrlState({ vehicleCategories: values.join(',') });
    },
    [setUrlState]
  );

  const resetFilters = useCallback(() => {
    resetUrlState();
    setOrganizationSearch('');
  }, [resetUrlState]);

  const removeFilter = useCallback(
    (key: string) => setUrlState({ [key]: DEFAULT_MAPPING_URL_STATE[key as keyof MappingUrlState] }),
    [setUrlState]
  );

  const removeVehicleCategory = useCallback(
    (value: string) =>
      setUrlState({ vehicleCategories: selectedVehicleCategories.filter((id) => id !== value).join(',') }),
    [selectedVehicleCategories, setUrlState]
  );

  const activeFilters = useMemo(
    () =>
      buildMappingFilterChips({
        consignmentNumber: urlState.consignmentNumber,
        organizationId: urlState.organizationId,
        organizationOptions,
        vehicleCategories: selectedVehicleCategories,
        vehicleCategoryOptions,
        onRemove: removeFilter,
        onRemoveVehicleCategory: removeVehicleCategory
      }),
    [
      urlState.consignmentNumber,
      urlState.organizationId,
      organizationOptions,
      selectedVehicleCategories,
      vehicleCategoryOptions,
      removeFilter,
      removeVehicleCategory
    ]
  );

  return {
    // Filter values
    consignmentNumber: urlState.consignmentNumber,
    /** Debounced twin of `consignmentNumber` — the one to send to the API. */
    debouncedConsignmentNumber,
    selectedOrganization: urlState.organizationId,
    selectedVehicleCategories,

    // Filter setters
    setConsignmentNumber,
    setSelectedOrganization,
    setSelectedVehicleCategories,

    // Organization search
    organizationSearch,
    setOrganizationSearch,
    isLoadingOrganizations,

    // Options for dropdowns
    organizationOptions,
    vehicleCategoryOptions,

    // Computed values
    filterValues,
    hasActiveFilters,
    activeFiltersCount,
    activeFilters,

    // Actions
    resetFilters
  };
}
